import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Checkbox } from "@components/input/Checkbox";
import { InputField } from "@components/input/InputField";
import { Select } from "@components/input/Select";
import { useApi } from "@hooks/useAPI";
import { useDocument } from "@hooks/useDocument";
import { useRecaptcha } from "@hooks/useRecaptcha";
import useTranslations from "@hooks/useTranslations";
import { useWindow } from "@hooks/useWindow";
import cn from "classnames";
import { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { MultiSelect } from "react-multi-select-component";
const SubscribeForm = (props) => {
    const { isV3, siteKey, redirectUrl, newsletters, showCompanyField, isCompanyRequired, showCountryField, isCountryRequired, showJobTitleField, isJobTitleRequired, addConsentToForm, countries } = props;
    const tokenKey = "RequestVerificationToken";
    const [apiErrors, setApiErrors] = useState(null);
    const [selectedSubscription, setSelectedSubscription] = useState([]);
    const [requestVerificationToken, setRequestVerificationToken] = useState(null);
    const newsletterGuids = !!selectedSubscription.length ? selectedSubscription.map(({ value }) => value) : [];
    const { t } = useTranslations();
    const { getToken } = useRecaptcha({ isV3, siteKey });
    const myWindow = useWindow();
    const api = useApi();
    const myDocument = useDocument();
    const required = t("BDO.LiveSite.SubscribeForm.Required");
    const emailPattern = { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: t("BDO.LiveSite.SubscribeForm.InvalidEmailFormat") };
    const { handleSubmit, formState: { errors }, control } = useForm();
    const getFormClassName = useCallback((name) => {
        var _a;
        const errorMessage = (_a = errors[name]) === null || _a === void 0 ? void 0 : _a.message;
        return cn("form-control", { "input-validation-error": !!errorMessage });
    }, [errors]);
    const sanitizeObject = (keyValue) => Object.keys(keyValue)
        // filter key value with null values
        .flatMap((key) => (!!keyValue[key] ? { [key]: keyValue[key] } : []))
        // reduce arrays of objects to one
        .reduce((prev, curr) => ({ ...prev, ...curr }), {});
    const onSubmit = async (data) => {
        const recaptchaToken = await getToken();
        if (!recaptchaToken) {
            setApiErrors(required);
            return;
        }
        try {
            const { jobTitle, email, firstName, lastName, company, countryGuid } = data;
            const validatedData = { firstName, lastName, email, company, countryGuid, jobTitle, newsletterGuids, recaptchaToken };
            const payload = sanitizeObject(validatedData);
            // actual post request
            const isSuccessful = await api
                .post(`api/${props.endpointRoute}`, {
                json: payload,
                ...(!!requestVerificationToken && { extraHeaders: { requestVerificationToken } })
            })
                .json();
            if (!isSuccessful) {
                throw Error("Something went wrong");
            }
            return redirect();
        }
        catch (error) {
            console.log({ error });
        }
    };
    const redirect = useCallback(() => {
        if (!!myWindow) {
            myWindow.location.href = redirectUrl;
        }
    }, [myWindow]);
    useEffect(() => {
        var _a, _b;
        if (!!myWindow) {
            setRequestVerificationToken((_b = (_a = myDocument.getElementById(tokenKey)) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : "");
        }
    }, [myWindow]);
    return (_jsxs("form", { onSubmit: handleSubmit(onSubmit), children: [_jsx(Controller, { name: "firstName", control: control, rules: { required }, render: ({ field }) => {
                    var _a;
                    return (_jsx(InputField, { className: getFormClassName("firstName"), name: "firstName", formLabel: t("BDO.LiveSite.SubscribeForm.FirstName"), errorMessage: (_a = errors.firstName) === null || _a === void 0 ? void 0 : _a.message, ...field }));
                } }), _jsx(Controller, { name: "lastName", control: control, rules: { required }, render: ({ field }) => {
                    var _a;
                    return (_jsx(InputField, { className: getFormClassName("lastName"), name: "lastName", formLabel: t("BDO.LiveSite.SubscribeForm.LastName"), errorMessage: (_a = errors.lastName) === null || _a === void 0 ? void 0 : _a.message, ...field }));
                } }), _jsx(Controller, { name: "email", control: control, rules: { required, pattern: emailPattern }, render: ({ field }) => {
                    var _a;
                    return (_jsx(InputField, { className: getFormClassName("email"), name: "email", formLabel: t("BDO.LiveSite.Email"), errorMessage: (_a = errors.email) === null || _a === void 0 ? void 0 : _a.message, ...field }));
                } }), showCompanyField && (_jsx(Controller, { name: "company", control: control, rules: { required: { message: required, value: isCompanyRequired } }, render: ({ field }) => {
                    var _a;
                    return (_jsx(InputField, { className: getFormClassName("company"), name: "company", formLabel: t("BDO.LiveSite.SubscribeForm.Company"), errorMessage: (_a = errors.company) === null || _a === void 0 ? void 0 : _a.message, ...field }));
                } })), showJobTitleField && (_jsx(Controller, { name: "jobTitle", control: control, rules: {
                    required: { message: required, value: isJobTitleRequired },
                    maxLength: { value: 50, message: t("BDO.LiveSite.SubscribeForm.InvalidJobTitle") }
                }, render: ({ field }) => {
                    var _a;
                    return (_jsx(InputField, { className: getFormClassName("jobTitle"), name: "jobTitle", formLabel: t("BDO.LiveSite.SubscribeForm.JobTitle"), errorMessage: (_a = errors.jobTitle) === null || _a === void 0 ? void 0 : _a.message, ...field }));
                } })), showCountryField && (_jsx(Controller, { name: "countryGuid", control: control, rules: { required: { message: required, value: isCountryRequired } }, render: ({ field }) => {
                    var _a;
                    return (_jsx(Select, { options: countries, className: getFormClassName("countryGuid"), name: "country", formLabel: t("BDO.LiveSite.SubscribeForm.Country"), errorMessage: (_a = errors.countryGuid) === null || _a === void 0 ? void 0 : _a.message, ...field }));
                } })), addConsentToForm && (_jsx(Controller, { name: "consent", control: control, rules: { required }, render: ({ field }) => {
                    var _a;
                    return (_jsx(Checkbox, { className: getFormClassName("consent"), name: "country", formLabel: t("BDO.LiveSite.SubscribeForm.Consent"), errorMessage: (_a = errors.consent) === null || _a === void 0 ? void 0 : _a.message, ...field }));
                } })), _jsx("div", { className: "form-field", children: _jsxs("div", { className: "multiselect-container", children: [_jsx("label", { htmlFor: "", children: t("BDO.LiveSite.SubscribeForm.SubscribeTo") }), _jsx(MultiSelect, { options: newsletters, className: "newsletter-multiselect", ArrowRenderer: () => (_jsx(_Fragment, { children: _jsx("svg", { height: "24px", width: "24px", viewBox: "0 0 24 24", fill: "%23000000", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" }) }) })), value: selectedSubscription, onChange: (options) => setSelectedSubscription(options), labelledBy: "newsletters-list", overrideStrings: {
                                allItemsAreSelected: t("BDO.LiveSite.MultiSelect.AllItemsAreSelected"),
                                clearSearch: t("BDO.LiveSite.MultiSelect.ClearSearch"),
                                clearSelected: t("BDO.LiveSite.MultiSelect.ClearSelected"),
                                noOptions: t("BDO.LiveSite.MultiSelect.NoOptions"),
                                search: t("BDO.LiveSite.MultiSelect.Search"),
                                selectAll: t("BDO.LiveSite.MultiSelect.SelectAll"),
                                selectAllFiltered: t("BDO.LiveSite.MultiSelect.SelectAllFiltered"),
                                selectSomeItems: t("BDO.LiveSite.SubscribeForm.SelectNewsletters"),
                                create: t("BDO.LiveSite.MultiSelect.Create")
                            } })] }) }), _jsx("br", {}), _jsx("div", { className: "g-recaptcha", "data-sitekey": siteKey, "data-size": isV3 ? "invisible" : "normal" }), _jsx("br", {}), !!apiErrors && _jsx("p", { className: "subscribe__error-message", children: apiErrors }), _jsx("input", { type: "submit", className: "btn", value: t("BDO.LiveSite.Submit") })] }));
};
export default SubscribeForm;
